/**
 * Client API verso il backend Quarkus.
 *
 * In questa prima fase usiamo un client `fetch` tipizzato a mano.
 * Appena il backend esporrà lo spec OpenAPI (già attivo su
 * /q/openapi), passeremo a openapi-typescript / openapi-fetch con:
 *
 *   npm run api:generate
 *
 * che genererà src/lib/api-schema.d.ts.
 */

const API_BASE =
  typeof window === "undefined"
    ? (process.env.API_URL_SERVER || process.env.NEXT_PUBLIC_API_URL || "http://localhost:8080")
    : "";                       // client-side: same-origin, proxied via /cms-api rewrite
const API_PREFIX = typeof window === "undefined" ? "/api" : "/cms-api";

const API_BASE_ORIGIN = API_BASE ? new URL(API_BASE).origin : "";
const API_BASE_PATH = API_BASE ? new URL(API_BASE).pathname.replace(/\/$/, "") : "";

function joinPath(basePath: string, path: string): string {
  const base = basePath.replace(/\/$/, "");
  const next = path.startsWith("/") ? path : `/${path}`;
  return `${base}${next}`.replace(/\/+/g, "/");
}

function ensureApiPrefix(path: string): string {
  const normalized = path.startsWith("/") ? path : `/${path}`;
  if (normalized === API_PREFIX || normalized.startsWith(`${API_PREFIX}/`)) {
    return normalized;
  }
  if (API_BASE_PATH === API_PREFIX || API_BASE_PATH.endsWith(`${API_PREFIX}`)) {
    return normalized;
  }
  return `${API_PREFIX}${normalized}`;
}

type Query = Record<string, string | number | boolean | undefined>;

function buildUrl(path: string, query?: Query): string {
  const fullPath = joinPath(API_BASE_PATH, ensureApiPrefix(path));
  const url = API_BASE_ORIGIN
    ? new URL(fullPath, API_BASE_ORIGIN)
    : new URL(fullPath, "http://placeholder");   // client-side: only path matters
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== null) url.searchParams.set(k, String(v));
    }
  }
  // Client-side: return relative path (same-origin); Server-side: full URL
  return API_BASE_ORIGIN ? url.toString() : `${url.pathname}${url.search}`;
}

async function request<T>(
  path: string,
  init: RequestInit & { query?: Query } = {},
): Promise<T> {
  const { query, ...rest } = init;
  const res = await fetch(buildUrl(path, query), {
    ...rest,
    headers: {
      "Content-Type": "application/json",
      ...(rest.headers ?? {}),
    },
    cache: "no-store",
  });
  if (!res.ok) {
    throw new Error(`API ${res.status} ${res.statusText} on ${path}`);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

// ---------- Mutazioni autenticate (client-side via /api/cms proxy) ---------

/**
 * Controlla se la risposta indica una sessione scaduta.
 * Se sì, redirige alla pagina di login.
 */
function handleExpiredSession(res: Response): boolean {
  if (res.status === 401 && typeof window !== "undefined") {
    // Prova a leggere il body per verificare se è un session_expired
    res.clone().json().then((body) => {
      if (body?.error === "session_expired") {
        alert("La sessione è scaduta. Verrai rediretto alla pagina di login.");
        window.location.href = "/api/auth/signin?callbackUrl=/admin";
      }
    }).catch(() => {
      // Anche senza body JSON, un 401 dal proxy = sessione scaduta
      alert("La sessione è scaduta. Verrai rediretto alla pagina di login.");
      window.location.href = "/api/auth/signin?callbackUrl=/admin";
    });
    return true;
  }
  return false;
}

/**
 * Esegue una richiesta di scrittura (POST / PUT / DELETE) attraverso il
 * route-handler /api/cms/[...path] che aggiunge il Bearer token Keycloak.
 *
 * Questa funzione è pensata per l'uso **client-side** (admin).
 */
async function mutate<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const normalized = path.startsWith("/") ? path : `/${path}`;
  const url = `/api/cms${normalized}`;
  const res = await fetch(url, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(init.headers ?? {}),
    },
  });
  if (!res.ok) {
    if (handleExpiredSession(res)) {
      // Blocca l'esecuzione: il redirect avverrà a breve
      return new Promise(() => {});
    }
    const body = await res.text().catch(() => "");
    throw new Error(
      body
        ? `Errore ${res.status}: ${body.slice(0, 300)}`
        : `Errore ${res.status} ${res.statusText}`,
    );
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  if (!text) return undefined as T;
  return JSON.parse(text) as T;
}

// ---------- Social publish (Meta Graph) ------------------------------------

/**
 * Risultato della pubblicazione su un social network.
 *
 * Stati possibili (allineati al backend Quarkus, vedi
 * /q/swagger-ui → SocialPublishResource):
 *   - ok=true:    pubblicazione riuscita, `postId` è il nuovo id Meta
 *   - ok=false con `httpStatus=409`: contenuto già pubblicato di recente
 *   - ok=false con `httpStatus=422`: skip volontario (skipReason: missing image, ecc.)
 *   - ok=false con `httpStatus=502`: Meta ha rifiutato (rate limit, token scaduto)
 *   - ok=false con `httpStatus=404`: contenuto non trovato
 *   - ok=false con `httpStatus=400`: status !== PUBLISHED
 */
export interface SocialPublishResult {
  ok: boolean;
  httpStatus: number;
  /** ID del post Meta (Facebook post id / Instagram media id) — solo su success. */
  postId?: string | null;
  /** Messaggio di errore leggibile (solo su failure). */
  error?: string | null;
  /** Motivo dello skip (solo su 422). */
  skipReason?: string | null;
  /** Body grezzo della risposta. */
  raw?: Record<string, unknown> | null;
}

/**
 * Esegue una richiesta POST per pubblicare un'entità su un social.
 * NON throwa: ritorna sempre un {@link SocialPublishResult} con il dettaglio.
 */
async function socialPublish(path: string): Promise<SocialPublishResult> {
  const url = `/api/cms${path.startsWith("/") ? path : `/${path}`}`;
  let res: Response;
  try {
    res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
    });
  } catch (err) {
    return {
      ok: false,
      httpStatus: 0,
      error: err instanceof Error ? err.message : "Errore di rete",
    };
  }
  if (handleExpiredSession(res)) {
    return { ok: false, httpStatus: 401, error: "Sessione scaduta" };
  }
  let body: Record<string, unknown> = {};
  try {
    const text = await res.text();
    body = text ? JSON.parse(text) : {};
  } catch {
    // body non JSON
  }
  if (res.ok) {
    return {
      ok: true,
      httpStatus: res.status,
      postId: (body.postId as string | undefined) ?? (body.mediaId as string | undefined) ?? null,
      raw: body,
    };
  }
  return {
    ok: false,
    httpStatus: res.status,
    error: (body.error as string | undefined) ?? (body.message as string | undefined) ?? `HTTP ${res.status}`,
    skipReason: (body.skipReason as string | undefined) ?? null,
    raw: body,
  };
}

export type SocialNetwork = "facebook" | "instagram";

// ---------- Tipi (manuali per ora, poi rimpiazzati da OpenAPI) ---------------

export interface PageResponse<T> {
  items: T[];
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
}

export interface MediaDto {
  id: number;
  filename: string;
  mimeType: string;
  byteSize: number;
  width: number | null;
  height: number | null;
  altText: string | null;
  url: string;
}

export interface CategoryDto {
  id: number;
  slug: string;
  name: string;
  description: string | null;
  parentId: number | null;
}

export interface TagDto {
  id: number;
  slug: string;
  name: string;
}

export interface PostDto {
  id: number;
  slug: string;
  title: string;
  excerpt: string | null;
  content: string | null;
  status: string;
  authorName: string | null;
  featuredMedia: MediaDto | null;
  publishedAt: string | null;
  categories: CategoryDto[];
  tags: TagDto[];
}

export interface PageDto {
  id: number;
  slug: string;
  title: string;
  content: string | null;
  status: string;
  template: string | null;
  menuOrder: number;
  parentId: number | null;
  authorName: string | null;
  featuredMedia: MediaDto | null;
  publishedAt: string | null;
}

export interface AiFeedbackSummary {
  id: number;
  rating: number;
  autoScore: number | null;
  combinedScore: number;
  similarityAiVsPublished: number | null;
  similarityExtractVsPublished: number | null;
  scoreDetails: string | null;
  notes: string | null;
  createdBy: string | null;
  /** [0..1] — scoring v2 breakdown (null se campo non valutabile o record v1) */
  scoreTitolo?: number | null;
  scoreAutori?: number | null;
  scoreData?: number | null;
  scoreFonte?: number | null;
  scoreContenuto?: number | null;
  /** TRUE se lo scoring non è attendibile (embedding mancanti). */
  requiresReview?: boolean;
  /** "v1" | "v2" */
  scoringVersion?: string | null;
}

export interface ApprofondimentoDto {
  id: number;
  slug: string;
  titolo: string;
  contenuto: string | null;
  data: string;
  status: string;
  urlEsterna: string | null;
  pdfAllegato: MediaDto | null;
  /** Immagine scelta manualmente dall'editor (null se non impostata). */
  immagine: MediaDto | null;
  /** Screenshot auto-generato dal backend (null se non catturato / confidence HIGH). */
  screenshot: MediaDto | null;
  /**
   * Immagine effettiva da usare come hero — calcolata dal backend in base a
   * {@link heroImagePreference}. Puo' combinare immagine+screenshot secondo
   * la preferenza editoriale. Null quando l'editor ha scelto HIDE o non c'e'
   * alcun media. Il frontend preferisce usare questo campo per rendering.
   */
  heroImage: MediaDto | null;
  /** Preferenza hero: AUTO | USE_SCREENSHOT | HIDE */
  heroImagePreference: string | null;
  /**
   * Visualizzazione dell'immagine nella pagina di dettaglio
   * (`/approfondimenti/[slug]`). Default lato frontend: HIDDEN —
   * l'immagine appare solo se l'editor sceglie esplicitamente di mostrarla.
   *  - HIDDEN   : nessuna immagine nel dettaglio
   *  - FULL     : immagine a tutta larghezza (legacy)
   *  - CENTERED : immagine centrata e contenuta (max-w ridotto)
   * Campo opzionale: in attesa di supporto backend, senza il campo il
   * dettaglio si comporta come HIDDEN.
   */
  detailImageMode?: "HIDDEN" | "FULL" | "CENTERED" | null;
  autori: string[];
  fonte: string | null;
  categories: CategoryDto[];
  /** JSON string from AI processing — parse with parseAiSuggestion() */
  aiSuggestionJson: string | null;
  /** First ~500 chars scraped from the external URL */
  textExtract: string | null;
  /**
   * Quale sorgente ha prodotto il contenuto pubblicato.
   *
   * Legacy (pre-C1):
   *   - AI_SUGGESTION  → proposta AI accettata senza validazione citazione
   *   - TEXT_EXTRACT   → editor ha preso il bodyText raw
   *   - MANUAL         → editor ha scritto tutto a mano
   *
   * Nuovi valori (post-C1, max 600 char + citazione verbatim):
   *   - AI_WITH_CITATION  → contenuto AI con citazione verbatim verificata (body o OCR)
   *   - AI_NO_CITATION    → contenuto AI senza citazione (la verifica non è passata)
   *   - AI_TRUNCATED      → contenuto AI troncato al limite dei 600 char
   *   - AI_LOW_CONFIDENCE → estrazione troppo povera, contenuto poco affidabile
   */
  descriptionSource: string | null;
  /** AI provider used: GEMINI | GROQ | OPENROUTER | FALLBACK_SCRAPING | null */
  aiProvider: string | null;
  /**
   * Punteggio 0-100 di confidence sull'estrazione della pagina sorgente (C8).
   * Null per approfondimenti pre-V19.
   */
  extractionConfidenceScore: number | null;
  /** Tier derivato dal punteggio: HIGH (≥70) / MEDIUM (≥40) / LOW (<40). */
  extractionConfidenceTier: "HIGH" | "MEDIUM" | "LOW" | null;
  /**
   * JSON breakdown dei contributi per dimensione. Parse on-demand con
   * {@link parseConfidenceBreakdown}.
   */
  extractionConfidenceBreakdown: string | null;
  /** Snapshot of the most recent AI feedback (null if none) */
  latestAiFeedback: AiFeedbackSummary | null;
  /**
   * Stato della pipeline async di ingestione (Q-FIX1). Valori:
   *  - SCREENSHOT_PENDING: in coda screenshot.requested
   *  - AI_PENDING: screenshot fatto, AI in coda
   *  - POST_PROCESSING: AI fatto, in attesa embedding + reply WhatsApp
   *  - PROCESSED: pipeline completa, tutto consegnato
   *  - FAILED: uno stage ha esaurito i retry
   *  - PENDING: legacy (sync)
   *  - null: l'approfondimento non viene da WhatsApp (creato manualmente)
   */
  pipelineStatus:
    | "SCREENSHOT_PENDING"
    | "AI_PENDING"
    | "POST_PROCESSING"
    | "PROCESSED"
    | "FAILED"
    | "PENDING"
    | null;

  // ── Social publishing (Meta Graph) ────────────────────────────────
  /** ID del post Facebook generato dall'ultima pubblicazione (null = mai). */
  facebookPostId?: string | null;
  /** Timestamp ISO dell'ultima pubblicazione su Facebook. */
  facebookPublishedAt?: string | null;
  /** ID del media Instagram generato dall'ultima pubblicazione (null = mai). */
  instagramMediaId?: string | null;
  /** Timestamp ISO dell'ultima pubblicazione su Instagram. */
  instagramPublishedAt?: string | null;
}

/**
 * Contributi per dimensione del punteggio di confidence (C8). Chiavi fisse
 * ma rappresentate come Record per robustezza se il backend aggiunge nuove
 * dimensioni in futuro.
 */
export type ConfidenceBreakdown = Record<string, number>;

export function parseConfidenceBreakdown(
  json: string | null | undefined,
): ConfidenceBreakdown | null {
  if (!json) return null;
  try {
    const parsed = JSON.parse(json);
    if (parsed && typeof parsed === "object") return parsed as ConfidenceBreakdown;
    return null;
  } catch {
    return null;
  }
}

/** Parsed shape of aiSuggestionJson */
export interface AiSuggestion {
  titolo?: string;
  contenuto?: string;
  data?: string;
  autori?: string[];
  fonte?: string;
  [key: string]: unknown;
}

/** Safely parse aiSuggestionJson */
export function parseAiSuggestion(json: string | null | undefined): AiSuggestion | null {
  if (!json) return null;
  try { return JSON.parse(json) as AiSuggestion; } catch { return null; }
}

export interface RelatoreDto {
  id: number;
  slug: string;
  nome: string;
  bio: string | null;
  avatar: MediaDto | null;
  /** V31: topics di override editoriale (lista vuota o null = AI fallback). */
  topics?: string[] | null;
  /** V32: false = pagina pubblica spenta (frontend deve renderizzare solo
   *  il nome, no link). Default true se mancante (per compat lato BE legacy). */
  hubEnabled?: boolean;
}

export interface RelatoreWithCountDto {
  id: number;
  slug: string;
  nome: string;
  eventCount: number;
  /** V32: default true. Quando false la pagina pubblica e' spenta e i
   *  componenti che linkano al hub devono renderizzare solo il nome. */
  hubEnabled?: boolean;
}

/**
 * Tipi di allegato media supportati da V13 (vedi EventoMediaItem.MediaType lato BE).
 * La stringa e' il name dell'enum Java: non cambiare casuali maiuscole/underscore.
 */
export type EventoMediaType =
  | "VIDEO_URL"
  | "VIDEO_FILE"
  | "PHOTO"
  | "GALLERY"
  | "PDF"
  | "SLIDE"
  | "NOTE"
  // Rassegna stampa: link esterno a un articolo di giornale online
  | "PRESS_URL"
  // Rassegna stampa: PDF/scansione di un articolo caricato sul CMS
  | "PRESS_FILE";

export interface EventoMediaItemDto {
  id: number;
  mediaType: EventoMediaType;
  /** File caricato. Null per i tipi "solo URL" (VIDEO_URL, PRESS_URL). */
  media: MediaDto | null;
  /** URL esterno. Valorizzato per i tipi "solo URL" (VIDEO_URL, PRESS_URL). */
  externalUrl: string | null;
  title: string | null;
  caption: string | null;
  position: number;
}

/**
 * Singolo articolo proposto dall'AI nella ricerca on-demand "rassegna stampa".
 * Ogni candidato è un articolo (o allegato) trovato online relativo all'evento.
 */
export interface PressCandidateDto {
  /** URL pubblico dell'articolo / risorsa. Sempre presente. */
  url: string;
  /** Titolo dell'articolo / risorsa, già pulito. */
  title: string;
  /** Testata o dominio sorgente (es. "Avvenire", "ilfattoquotidiano.it"). */
  source: string | null;
  /** Data di pubblicazione ISO-8601 se rilevata, altrimenti null. */
  publishedAt: string | null;
  /** Estratto / snippet di anteprima. */
  snippet: string | null;
  /** 0..1 — confidenza che l'articolo riguardi davvero l'evento. */
  confidence: number;
  /** Tipo di allegato suggerito (di default PRESS_URL). */
  suggestedType: EventoMediaType;
  /** MIME type se rilevato (utile per distinguere PDF). */
  mimeType: string | null;
  /** URL di un'eventuale thumbnail. */
  thumbnailUrl: string | null;
}

export interface EventoPressSearchResultDto {
  /** Provider AI usato (GEMINI / GROQ / OPENROUTER / FALLBACK_SCRAPING). */
  aiProvider: string | null;
  /** Timestamp ISO della generazione. */
  generatedAt: string;
  /** Articoli proposti, ordinati per confidence decrescente. */
  candidates: PressCandidateDto[];
}

/**
 * EventoEsterno (eventi suggeriti). Shape speculare al backend
 * {@code EventoEsternoDTO}: nomi in inglese, {@code relatori}/{@code promotori}
 * testo libero, locandina come media-id (risolto lato frontend con
 * {@code resolveMediaUrl}). Separato dai nostri {@link EventoDto}.
 */
export interface EventoEsternoDto {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  contenuto: string | null;
  status: string;
  data: string; // ISO date "YYYY-MM-DD"
  dataFine: string | null;
  oraInizio: string | null; // "HH:mm[:ss]"
  oraFine: string | null;
  location: string | null;
  address: string | null;
  organizzatori: string | null;
  promotori: string | null;
  relatori: string | null;
  videoUrl: string | null;
  linkUrl: string | null;
  daNonPerdere: boolean;
  heroCarousel: boolean;
  latitude: number | null;
  longitude: number | null;
  featuredMediaId: string | null;
  fotoMediaId: string | null;
  volantinoMediaId: string | null;
}

export interface EventoDto {
  id: number;
  slug: string;
  titolo: string;
  contenuto: string | null;
  descrizione: string | null;
  data: string;
  luogo: string;
  organizzatori: string | null;
  videoUrl: string | null;
  linkUrl: string | null;
  daNonPerdere: boolean;
  volantino: MediaDto | null;
  foto: MediaDto | null;
  gallery: MediaDto[];
  documenti: MediaDto[];
  relatori: RelatoreDto[];
  /**
   * V13: allegati generici ordinati. La summary (lista eventi) li include per
   * poter renderizzare l'icon-strip nella card homepage; la full (dettaglio)
   * li usa per renderizzare ogni allegato inline.
   */
  mediaItems: EventoMediaItemDto[];

  /**
   * Opzioni di visualizzazione della pagina dettaglio evento.
   * Tutte opzionali e retro-compatibili (backend può non averle: in quel caso
   * la UI applica i default).
   *
   * NOTA BACKEND: per persistirle serve estendere l'entity Evento con questi
   * 3 campi (VARCHAR(32), INTEGER, VARCHAR(32)) + le colonne in EventoDto/Input.
   */
  sidebarMode?: "compact" | "full" | "hidden";
  sidebarScrollMs?: number;
  mediaPosition?: "above" | "below" | "hidden";

  /**
   * E3 — campi temporali aggiuntivi popolati dall'AI extraction da
   * locandina (oppure inseriti manualmente in admin/eventi).
   * Tutti opzionali: gli eventi storici pre-V26 hanno questi a null.
   */
  oraInizio?: string | null;   // "HH:mm"
  oraFine?: string | null;     // "HH:mm"
  dataFine?: string | null;    // ISO date "YYYY-MM-DD"; null = single-day
  indirizzo?: string | null;   // Via, civico, CAP, città

  /** Provider AI (GEMINI/CEREBRAS/...) che ha generato la proposta — readonly. */
  aiProvider?: string | null;
  /** Marker stato proposta AI — readonly, riferimento per audit. */
  descriptionSource?: string | null;
  /** Slug status (DRAFT, PUBLISHED, ...) — opzionale per retrocompat. */
  status?: string;

  // ── Social publishing (Meta Graph) ────────────────────────────────
  /** ID del post Facebook generato dall'ultima pubblicazione (null = mai). */
  facebookPostId?: string | null;
  /** Timestamp ISO dell'ultima pubblicazione su Facebook. */
  facebookPublishedAt?: string | null;
  /** ID del media Instagram generato dall'ultima pubblicazione (null = mai). */
  instagramMediaId?: string | null;
  /** Timestamp ISO dell'ultima pubblicazione su Instagram. */
  instagramPublishedAt?: string | null;
}

// ---------- Tipi Input (payload per creazione / aggiornamento) ---------------

export interface ApprofondimentoInput {
  titolo: string;
  slug?: string;
  data: string;
  contenuto?: string;
  fonte?: string;
  urlEsterna?: string;
  autori?: string[];
  categoryIds?: number[];
  /** AI_SUGGESTION | TEXT_EXTRACT | MANUAL */
  descriptionSource?: string;
  /**
   * Visualizzazione immagine nella pagina di dettaglio.
   * HIDDEN (default) | FULL | CENTERED. Vedi ApprofondimentoDto.detailImageMode.
   */
  detailImageMode?: "HIDDEN" | "FULL" | "CENTERED";
}

export interface EventoInput {
  titolo: string;
  slug?: string;
  data: string;
  luogo?: string;
  contenuto?: string;
  descrizione?: string;
  organizzatori?: string;
  videoUrl?: string;
  linkUrl?: string;
  daNonPerdere?: boolean;
  volantinoMediaId?: number;
  fotoMediaId?: number;
  relatoreIds?: number[];
  /** Opzioni di visualizzazione pagina — vedi EventoDto */
  sidebarMode?: "compact" | "full" | "hidden";
  sidebarScrollMs?: number;
  mediaPosition?: "above" | "below" | "hidden";
  /** E3 — campi temporali/indirizzo aggiuntivi (opzionali). */
  oraInizio?: string | null;
  oraFine?: string | null;
  dataFine?: string | null;
  indirizzo?: string | null;
}

export interface PostInput {
  title: string;
  slug?: string;
  content?: string;
  excerpt?: string;
  status?: string;
  categoryIds?: number[];
  tagIds?: number[];
  featuredMediaId?: number;
}

export interface PageInput {
  title: string;
  slug?: string;
  content?: string;
  status?: string;
  template?: string;
  menuOrder?: number;
  parentId?: number | null;
}

export interface RelatoreInput {
  nome: string;
  slug?: string;
  bio?: string;
  avatarMediaId?: number;
  /** V31: topics override editoriali. Null/undefined = preserve esistente.
   *  Lista vuota = clear (fallback AI extraction). */
  topics?: string[];
  /** V32: se false la pagina pubblica del relatore e' spenta (404 + skip sitemap).
   *  Null/undefined = preserve esistente. */
  hubEnabled?: boolean;
}

export interface CategoryInput {
  name: string;
  slug?: string;
  description?: string;
  displayOrder?: number;
  parentId?: number | null;
}

export interface AiFeedbackDto {
  id: number;
  approfondimentoId: number;
  approfondimentoTitolo: string | null;
  approfondimentoSlug: string | null;
  rating: number;
  notes: string | null;
  createdBy: string | null;
  createdAt: string;
  hasAiOriginal: boolean;
  hasPublishedVersion: boolean;
  autoScore: number | null;
  combinedScore: number | null;
  /** JSON string with scoring breakdown details */
  scoreDetails: string | null;
  /** Cosine similarity (0..1) between AI proposal and published content */
  similarityAiVsPublished: number | null;
  /** Cosine similarity (0..1) between the 500-char extract and published content */
  similarityExtractVsPublished: number | null;
  /** Extracted from scoreDetails on backend */
  dateAccuracy: boolean | null;
  /** Extracted from scoreDetails on backend */
  authorAccuracy: boolean | null;
  /** From Approfondimento.descriptionSource */
  descriptionSource: string | null;

  // ── Scoring v2 breakdown ────────────────────────────────────────
  /** [0..1] qualità estrazione titolo. Null se campo non valutabile. */
  scoreTitolo: number | null;
  /** [0..1] qualità estrazione autori. Null se campo non valutabile. */
  scoreAutori: number | null;
  /** [0..1] correttezza data articolo. Null se campo non valutabile. */
  scoreData: number | null;
  /** [0..1] correttezza fonte. Null se campo non valutabile. */
  scoreFonte: number | null;
  /** [0..1] similarità semantica contenuto. Null se embedding mancante. */
  scoreContenuto: number | null;
  /** TRUE se lo scoring non è attendibile — la UI deve mostrare un avviso. */
  requiresReview: boolean;
  /** Versione algoritmo scoring: "v1" legacy, "v2" nuovo probabilistico. */
  scoringVersion: string | null;
}

/** Parsed scoreDetails */
export interface ScoreDetails {
  /** Cosine similarity between AI proposal and published content (0..1) */
  vectorSimilarity?: number;
  /** Cosine similarity between the 500-char extract and the published content (0..1) */
  extractSimilarity?: number;
  /** True when the 500-char extract is closer to the published version than the AI proposal */
  extract_better_than_ai?: boolean;
  /** Jaccard fallback word-level similarity on titles (0..1) */
  title_similarity?: number;
  /** Jaccard fallback word-level similarity on content (0..1) — used when no embedding */
  content_jaccard?: number;
  dateAccuracy?: boolean;
  authorAccuracy?: boolean;
  descriptionSource?: string;
  penalties?: string[];
  [key: string]: unknown;
}

export function parseScoreDetails(json: string | null | undefined): ScoreDetails | null {
  if (!json) return null;
  try { return JSON.parse(json) as ScoreDetails; } catch { return null; }
}

export interface AiFeedbackInput {
  approfondimentoId: number;
  rating: number;
  notes?: string;
}

export interface AiFeedbackStats {
  totalFeedback: number;
  averageRating: number;
  averageAutoScore?: number | null;
  ratingDistribution: Record<string, number>;
  dateAccuracy?: { correct: number; wrong: number };
  authorAccuracy?: { correct: number; wrong: number };
  [key: string]: unknown;
}

/**
 * Riga della dashboard /admin/ai-stats: una per provider AI
 * (GEMINI / CEREBRAS / MISTRAL / GITHUB / OPENROUTER /
 * FALLBACK_SCRAPING / EXTRACTION_TOO_SHORT / USER_SUPPLIED / UNKNOWN).
 *
 * I contatori spaccano il totale per il valore di
 * Approfondimento.descriptionSource al momento della creazione.
 *
 * `citationRate` è withCitation/total nello stesso intervallo, oppure
 * null se total=0 (provider mai chiamato nella finestra selezionata).
 */
export interface AiStatsProviderRow {
  provider: string;
  total: number;
  withCitation: number;
  /** D5/D6 — citazioni recuperate dal self-repair pass (post-validate fail). */
  repaired: number;
  noCitation: number;
  truncated: number;
  lowConfidence: number;
  userSupplied: number;
  other: number;
  /** (withCitation + repaired) / total. */
  citationRate: number | null;
}

/** Risposta di GET /admin/ai-stats[?days=N]. */
export interface AiStatsResponse {
  providers: AiStatsProviderRow[];
  grandTotal: number;
  grandWithCitation: number;
  overallCitationRate: number | null;
  /** Numero di giorni di finestra; null = tutto lo storico. */
  windowDays: number | null;
}

/**
 * Payload del POST /approfondimenti/by-id/{id}/ai-correction.
 * Tutti i campi sono opzionali: per i campi null il backend mantiene il
 * valore attualmente salvato sull'approfondimento.
 */
export interface AiCorrectionRequest {
  titolo?: string | null;
  contenuto?: string | null;
  fonte?: string | null;
  autori?: string[] | null;
  data?: string | null;   // ISO date (YYYY-MM-DD)
  note?: string | null;
}

/** DTO di lettura di ai_correction_memory. */
export interface AiCorrectionMemoryDto {
  id: number;
  approfondimentoId: number;
  approfondimentoTitolo: string | null;
  approfondimentoSlug: string | null;

  aiTitolo: string | null;
  aiContenuto: string | null;
  aiFonte: string | null;
  aiAutori: string | null;
  aiData: string | null;

  correctedTitolo: string | null;
  correctedContenuto: string | null;
  correctedFonte: string | null;
  correctedAutori: string | null;
  correctedData: string | null;

  titoloChanged: boolean;
  contenutoChanged: boolean;
  fonteChanged: boolean;
  autoriChanged: boolean;
  dataChanged: boolean;

  correctionNote: string | null;
  aiProvider: string | null;
  createdAt: string;
  createdBy: string | null;
}

/** Risposta dell'endpoint POST ai-correction. */
export interface AiCorrectionResponse {
  approfondimento: ApprofondimentoDto;
  correctionMemoryId: number;
}

/** Metriche aggregate sulla memoria correzioni (dashboard admin). */
export interface AiCorrectionsStatsDto {
  total: number;
  withChanges: number;
  titoloChangedCount: number;
  contenutoChangedCount: number;
  fonteChangedCount: number;
  autoriChangedCount: number;
  dataChangedCount: number;
  last30Days: number;
}

/** Filtri opzionali per la lista admin correzioni. */
export interface AiCorrectionsListFilters {
  page?: number;
  size?: number;
  hasChanges?: boolean;
  changedField?: "titolo" | "contenuto" | "fonte" | "autori" | "data";
  fromDate?: string;  // ISO YYYY-MM-DD
  toDate?: string;    // ISO YYYY-MM-DD
  search?: string;
  createdBy?: string;
}

export type ApprofondimentoStatus = "PROPOSED" | "PUBLISHED" | "REJECTED" | "DRAFT";

/**
 * DTO WhatsApp allineato allo swagger del backend Quarkus.
 *   /q/swagger-ui/#/Whats%20App%20Webhook%20Resource
 *
 * Il payload del webhook WAHA/WhatsApp Business può arrivare in due forme:
 *  - event "message"  → nuovo messaggio in entrata (body + pushName)
 *  - event "ack"      → aggiornamento stato di un messaggio inviato
 *                       ack = 1 server, 2 device, 3 read, 4 played
 *  - event "message.any" / "message.reaction" ecc.
 */
export interface WhatsAppMessageDto {
  messageId?: string | null;
  chatId?: string | null;
  /** Nome visualizzato WhatsApp del mittente (se disponibile) */
  pushName?: string | null;
  /** Numero del mittente (chatId senza suffisso) */
  from?: string | null;
  /** Testo messaggio — alias di messageBody */
  body?: string | null;
  /** Testo messaggio (campo originale backend) */
  messageBody?: string | null;
  /** Tipo evento: message | ack | message.reaction | … */
  event?: string | null;
  /**
   * Stato di consegna stile WhatsApp:
   *  0 pending / ERROR
   *  1 inviato al server      (singola spunta grigia)
   *  2 consegnato al device   (doppia spunta grigia)
   *  3 letto                  (doppia spunta blu)
   *  4 ascoltato (audio)
   */
  ack?: number | null;
  /** Timestamp epoch (secondi o ms, a seconda del provider) */
  timestamp?: number | null;
  /** Payload raw del webhook (JSON arbitrario). */
  payload?: Record<string, unknown> | null;
}

export interface WhatsAppHealthDto {
  status: string;
  [key: string]: unknown;
}

/** Stato consegna WhatsApp: 0..4 */
export type WhatsAppAck = 0 | 1 | 2 | 3 | 4;

/** Etichetta leggibile per il codice ack WhatsApp. */
export function whatsAppAckLabel(ack: number | null | undefined): string {
  switch (ack) {
    case 1: return "Inviato";
    case 2: return "Consegnato";
    case 3: return "Letto";
    case 4: return "Ascoltato";
    case 0: return "In attesa";
    default: return "—";
  }
}

/**
 * Riga della tabella admin "URL ricevuti via WhatsApp":
 * collega il messaggio inbound (chi ha inviato l'URL) con l'approfondimento creato
 * e con la risposta outbound del bot (e il suo stato di consegna/lettura).
 *
 * Allineato al record `ProcessedUrl` del backend (step 8 del log Claude Cowork).
 * Il backend potrebbe esporre alcuni campi come nested object — la UI accetta
 * entrambe le forme (flat o nested) tramite i getter in `lib/whatsapp-processed-url.ts`.
 */
export interface WhatsAppProcessedUrlDto {
  id?: number;
  url?: string | null;

  /* ── Mittente inbound ── */
  messageId?: string | null;
  chatId?: string | null;
  /** Nome WhatsApp del mittente (pushName) */
  senderName?: string | null;
  /**
   * WA-IMP5: URL CDN WhatsApp foto profilo / logo gruppo del mittente,
   * persistito dal backend al momento dell'inbound. Se popolato, il
   * frontend lo usa direttamente (no piu' lookup lazy via api.whatsappAvatar).
   */
  senderPictureUrl?: string | null;
  /** Timestamp (epoch ms o ISO) della ricezione */
  createdAt?: string | number | null;
  processedAt?: string | number | null;

  /* ── Approfondimento creato (se andato a buon fine) ── */
  approfondimentoId?: number | null;
  approfondimentoSlug?: string | null;
  approfondimentoTitolo?: string | null;
  approfondimentoStatus?: string | null;
  approfondimento?: {
    id?: number;
    slug?: string;
    titolo?: string;
    status?: string;
  } | null;

  /* ── Risposta outbound del bot ── */
  replyMessageId?: string | null;
  /** 0 pending · 1 inviato · 2 consegnato · 3 letto · 4 ascoltato */
  replyAck?: number | null;
  replyChatId?: string | null;
  replySentAt?: string | number | null;
  replyDeliveredAt?: string | number | null;
  replyReadAt?: string | number | null;
  replyError?: string | null;
  /** Alternativa nested */
  reply?: {
    messageId?: string | null;
    ack?: number | null;
    chatId?: string | null;
    sentAt?: string | number | null;
    deliveredAt?: string | number | null;
    readAt?: string | number | null;
    error?: string | null;
  } | null;
}

// ---------- Home Blocks (V15: gallery homepage configurabili) --------------
/**
 * Sistema di blocchi homepage (introdotto dalla migration V15 del backend).
 *
 * Ogni `HomeBlock` è una "gallery" configurabile dall'admin con 4 tipi:
 *   - IMAGE_GALLERY: item con `media` o `externalUrl` (immagini)
 *   - VIDEO_GALLERY: item con `externalUrl` (YouTube/Vimeo) o `media` (file video)
 *   - AUDIO_GALLERY: item con `externalUrl` (Spotify embed) o `media` (MP3)
 *   - QUOTE_GALLERY: item con `quoteText` + `quoteAuthor` + `quoteSource`
 *
 * I blocchi sono ordinati per `position` e filtrati per `enabled=true` nella
 * versione pubblica. Ogni item ha il proprio `position` gestito dal backend.
 */
export type HomeBlockType =
  | "IMAGE_GALLERY"
  | "VIDEO_GALLERY"
  | "AUDIO_GALLERY"
  | "QUOTE_GALLERY";

/**
 * Configurazione layout/rendering del blocco. Schema non vincolato lato backend
 * (JSON libero), ma il frontend riconosce queste chiavi:
 *   - layout: "grid" | "carousel" | "strip"
 *   - columns: 2 | 3 | 4  (solo per layout grid)
 *   - autoplay: boolean   (solo video/audio)
 *   - bgColor: string     (fascia a tutta larghezza)
 */
export interface HomeBlockConfig {
  layout?: "grid" | "carousel" | "strip";
  columns?: 2 | 3 | 4;
  autoplay?: boolean;
  bgColor?: string;
  [key: string]: unknown;
}

export interface HomeBlockItemDto {
  id: number;
  position: number;
  /** File caricato sul CMS (media upload). Null per item "solo URL". */
  media: MediaDto | null;
  /** URL esterno (YouTube/Vimeo per video, Spotify/MP3 per audio, URL immagine). */
  externalUrl: string | null;
  /** Didascalia opzionale mostrata sotto il media. */
  caption: string | null;
  /** Solo QUOTE_GALLERY: testo della citazione. */
  quoteText: string | null;
  /** Solo QUOTE_GALLERY: autore della citazione. */
  quoteAuthor: string | null;
  /** Solo QUOTE_GALLERY: fonte/opera della citazione. */
  quoteSource: string | null;
  /** Override per singolo item (es. colspan, variant). */
  configJson: Record<string, unknown> | null;
}

export interface HomeBlockDto {
  id: number;
  type: HomeBlockType;
  title: string | null;
  subtitle: string | null;
  position: number;
  enabled: boolean;
  configJson: HomeBlockConfig | null;
  items: HomeBlockItemDto[];
}

/** Payload per creare/aggiornare un blocco (senza items — si gestiscono a parte). */
export interface HomeBlockInput {
  type: HomeBlockType;
  title?: string | null;
  subtitle?: string | null;
  enabled?: boolean;
  configJson?: HomeBlockConfig | null;
}

/** Payload per creare/aggiornare un item. */
export interface HomeBlockItemInput {
  mediaId?: number | null;
  externalUrl?: string | null;
  caption?: string | null;
  quoteText?: string | null;
  quoteAuthor?: string | null;
  quoteSource?: string | null;
  configJson?: Record<string, unknown> | null;
}

// ---------- Media cleanup ----------------------------------------------------
//
// Risultato standard di una pulizia (singolo approfondimento o globale).
// `dryRun` e' presente solo nelle preview globali; per le pulizie eseguite
// e per quella per-approfondimento e' false / undefined.

export interface MediaCleanupResult {
  /** Path relativi (rispetto a storageDir) dei PNG cancellati, es. "screenshots/foo.png". */
  deletedPngs: string[];
  /** Path relativi degli HTML prefetch cancellati, es. "html/page_xxx.html". */
  deletedHtml: string[];
  /** Bytes liberati totali. */
  freedBytes: number;
  /** Bytes liberati arrotondati in MB (precomputato server-side per la UI). */
  freedMb: number;
  /** Durata della scansione + cancellazione in millisecondi. */
  durationMs: number;
  /** True se era una preview/dry-run (nessuna cancellazione effettiva). */
  dryRun?: boolean;
  /** Conteggio totale (PNG + HTML). */
  totalDeleted: number;
}

/** Stato della schedulazione oraria + snapshot dell'ultimo run automatico. */
export interface MediaCleanupScheduleState {
  active: boolean;
  /** Timestamp ISO dell'ultima esecuzione automatica (assente se mai eseguita). */
  lastRunAt?: string;
  /** Snapshot dell'ultimo run automatico (assente se mai eseguito). */
  lastRun?: MediaCleanupResult;
}

// ---------- AI Provider Chain (admin) -----------------------------------------

/**
 * Scope della catena di provider AI: i base mode (TEXT/OCR) e i contesti.
 * Un contesto senza override eredita dal base mode, e questo dal default.
 */
export type AiChainScope =
  | "TEXT"
  | "OCR"
  | "ANALYSIS"
  | "EMBEDDINGS"
  | "CITATIONS"
  | "PRESS_SEARCH"
  | "TOPICS";

/** Voce provider nell'ordine della chain (la posizione = indice nell'array). */
export interface AiChainProviderEntry {
  provider: string;
  enabled: boolean;
}

/** Ordine effettivo dei provider per ogni scope (chiave = nome scope). */
export type AiChainSettings = Record<string, AiChainProviderEntry[]>;

/** Provider AI configurato da admin (registry dinamico). La API key non è mai esposta. */
export interface AiProviderConfig {
  name: string;
  baseUrl: string;
  model: string;
  ocrModel: string | null;
  embeddingModel: string | null;
  chatCompletionsPath: string | null;
  enabled: boolean;
  /** true se è memorizzata una API key (cifrata). */
  apiKeySet: boolean;
}

/** Payload create/update provider. apiKey vuota/assente = mantieni quella esistente. */
export interface AiProviderConfigInput {
  baseUrl: string;
  apiKey?: string;
  model: string;
  ocrModel?: string | null;
  embeddingModel?: string | null;
  chatCompletionsPath?: string | null;
  enabled: boolean;
}

/** Tipo logico di un'impostazione amministrativa. */
export type SettingType = "STRING" | "BOOLEAN" | "INTEGER" | "DOUBLE" | "CSV" | "SECRET";

/** Vista di un'impostazione amministrativa (segreti mascherati). */
export interface AppSettingView {
  key: string;
  label: string;
  category: string;
  type: SettingType;
  secret: boolean;
  critical: boolean;
  /** Valore effettivo (null per i segreti). */
  value: string | null;
  /** Per i segreti: true se una key è impostata. */
  secretSet: boolean;
  /** true se esiste un override a DB (altrimenti vale env/default). */
  overridden: boolean;
  updatedBy: string | null;
  updatedAt: string | null;
}

// ---------- API ---------------------------------------------------------------

export const api = {
  // ── Posts (articoli) ─────────────────────────────────────────────
  posts: {
    list: (query?: { page?: number; size?: number; category?: string; tag?: string }) =>
      request<PageResponse<PostDto>>("/posts", { query }),
    bySlug: (slug: string) => request<PostDto>(`/posts/${slug}`),
    create: (data: PostInput) =>
      mutate<PostDto>("/posts", { method: "POST", body: JSON.stringify(data) }),
    update: (slug: string, data: PostInput) =>
      mutate<PostDto>(`/posts/${slug}`, { method: "PUT", body: JSON.stringify(data) }),
    delete: (slug: string) =>
      mutate<void>(`/posts/${slug}`, { method: "DELETE" }),
  },

  // ── Pages (pagine statiche) ─────────────────────────────────────
  pages: {
    list: (query?: { page?: number; size?: number; all?: boolean }) =>
      request<PageResponse<PageDto>>("/pages", { query }),
    bySlug: (slug: string) => request<PageDto>(`/pages/${slug}`),
    create: (data: PageInput) =>
      mutate<PageDto>("/pages", { method: "POST", body: JSON.stringify(data) }),
    update: (slug: string, data: PageInput) =>
      mutate<PageDto>(`/pages/${slug}`, { method: "PUT", body: JSON.stringify(data) }),
    delete: (slug: string) =>
      mutate<void>(`/pages/${slug}`, { method: "DELETE" }),
  },

  // ── Categorie ───────────────────────────────────────────────────
  categories: {
    list: () => request<CategoryDto[]>("/categories"),
    bySlug: (slug: string) => request<CategoryDto>(`/categories/${slug}`),
    create: (data: CategoryInput) =>
      mutate<CategoryDto>("/categories", { method: "POST", body: JSON.stringify(data) }),
    update: (id: number, data: CategoryInput) =>
      mutate<CategoryDto>(`/categories/${id}`, { method: "PUT", body: JSON.stringify(data) }),
    delete: (id: number) =>
      mutate<void>(`/categories/${id}`, { method: "DELETE" }),
  },

  // ── Tags ────────────────────────────────────────────────────────
  tags: {
    list: () => request<TagDto[]>("/tags"),
    bySlug: (slug: string) => request<TagDto>(`/tags/${slug}`),
  },

  // ── Approfondimenti ─────────────────────────────────────────────
  approfondimenti: {
    list: (query?: { page?: number; size?: number; category?: string }) =>
      request<PageResponse<ApprofondimentoDto>>("/approfondimenti", { query }),
    /** Lista admin con TUTTI gli stati (richiede auth) */
    adminAll: (query?: { page?: number; size?: number; status?: string }) =>
      mutate<PageResponse<ApprofondimentoDto>>(`/approfondimenti/admin/all?${new URLSearchParams(
        Object.fromEntries(
          Object.entries(query ?? {}).filter(([, v]) => v !== undefined).map(([k, v]) => [k, String(v)])
        )
      ).toString()}`, { method: "GET" }),
    /** Get single approfondimento by ID (admin, richiede auth) */
    adminById: (id: number) =>
      mutate<ApprofondimentoDto>(`/approfondimenti/admin/byId/${id}`, { method: "GET" }),
    /**
     * Get by slug with fallback: if /approfondimenti/{slug} returns 404,
     * search through the list endpoint (the backend get-by-slug is unreliable
     * for recently created items).
     */
    bySlug: async (slug: string): Promise<ApprofondimentoDto> => {
      try {
        return await request<ApprofondimentoDto>(`/approfondimenti/${slug}`);
      } catch (err) {
        // Fallback: search in paginated list
        const msg = err instanceof Error ? err.message : "";
        if (msg.includes("404") || msg.includes("Not Found")) {
          // Search through pages (server-side, public endpoint)
          for (let p = 0; p < 20; p++) {
            const page = await request<PageResponse<ApprofondimentoDto>>("/approfondimenti", {
              query: { page: p, size: 50 },
            });
            const found = page.items.find((a) => a.slug === slug);
            if (found) return found;
            if (p >= page.totalPages - 1) break;
          }
        }
        throw err;
      }
    },
    create: (data: ApprofondimentoInput) =>
      mutate<ApprofondimentoDto>("/approfondimenti", { method: "POST", body: JSON.stringify(data) }),
    update: (slug: string, data: ApprofondimentoInput) =>
      mutate<ApprofondimentoDto>(`/approfondimenti/${slug}`, { method: "PUT", body: JSON.stringify(data) }),
    /** Update by ID (more reliable than by slug) */
    updateById: (id: number, data: ApprofondimentoInput) =>
      mutate<ApprofondimentoDto>(`/approfondimenti/by-id/${id}`, { method: "PUT", body: JSON.stringify(data) }),
    delete: (slug: string) =>
      mutate<void>(`/approfondimenti/${slug}`, { method: "DELETE" }),
    deleteById: (id: number) =>
      mutate<void>(`/approfondimenti/by-id/${id}`, { method: "DELETE" }),
    /** Cambia stato (PROPOSED → PUBLISHED / REJECTED ecc.) */
    changeStatus: (slug: string, status: ApprofondimentoStatus) =>
      mutate<ApprofondimentoDto>(`/approfondimenti/${slug}/status`, {
        method: "PATCH",
        body: JSON.stringify({ status }),
      }),
    /** Cambia stato by ID (più affidabile dello slug) */
    changeStatusById: (id: number, status: ApprofondimentoStatus) =>
      mutate<ApprofondimentoDto>(`/approfondimenti/by-id/${id}/status`, {
        method: "PATCH",
        body: JSON.stringify({ status }),
      }),

    /**
     * Certifica una correzione editoriale: applica le modifiche all'approfondimento
     * E crea una riga in ai_correction_memory (con embedding del testo fonte) che
     * verra' usata come few-shot RAG nel prompt AI dei prossimi articoli simili.
     */
    submitAiCorrection: (id: number, data: AiCorrectionRequest) =>
      mutate<AiCorrectionResponse>(`/approfondimenti/by-id/${id}/ai-correction`, {
        method: "POST",
        body: JSON.stringify(data),
      }),

    /** Lista le correzioni AI memorizzate per un approfondimento (audit trail). */
    listAiCorrections: (id: number) =>
      mutate<AiCorrectionMemoryDto[]>(`/approfondimenti/by-id/${id}/ai-corrections`, {
        method: "GET",
      }),

    /**
     * Lista GLOBALE paginata di tutte le correzioni AI certificate (dashboard admin).
     * Supporta filtri per presenza di modifiche, campo cambiato, date range e
     * ricerca testuale sulla nota editoriale.
     */
    adminListAiCorrections: (filters?: AiCorrectionsListFilters) => {
      const params = new URLSearchParams();
      if (filters?.page !== undefined)    params.set("page", String(filters.page));
      if (filters?.size !== undefined)    params.set("size", String(filters.size));
      if (filters?.hasChanges !== undefined) params.set("hasChanges", String(filters.hasChanges));
      if (filters?.changedField)          params.set("changedField", filters.changedField);
      if (filters?.fromDate)              params.set("fromDate", filters.fromDate);
      if (filters?.toDate)                params.set("toDate", filters.toDate);
      if (filters?.search)                params.set("search", filters.search);
      if (filters?.createdBy)             params.set("createdBy", filters.createdBy);
      const qs = params.toString();
      const path = qs
        ? `/approfondimenti/admin/ai-corrections?${qs}`
        : `/approfondimenti/admin/ai-corrections`;
      return mutate<PageResponse<AiCorrectionMemoryDto>>(path, { method: "GET" });
    },

    /** Metriche aggregate per la dashboard globale correzioni. */
    adminAiCorrectionsStats: () =>
      mutate<AiCorrectionsStatsDto>(`/approfondimenti/admin/ai-corrections/stats`, {
        method: "GET",
      }),

    // ── Media allegati (immagine manuale, PDF, screenshot) ────────
    //
    // regenerateScreenshot richiama il backend che a sua volta scarica via
    // ScreenshotService. Puo' impiegare fino a ~45s sulla prima cattura di
    // una SPA (cache-warming retry lato backend). setImmagine / setPdf
    // assumono che il file sia stato caricato prima con api.media.upload()
    // e che il chiamante abbia quindi un mediaId.

    /** (Ri)genera lo screenshot scaricando dalla urlEsterna dell'approfondimento. */
    regenerateScreenshot: (id: number) =>
      mutate<ApprofondimentoDto>(`/approfondimenti/by-id/${id}/screenshot/regenerate`, {
        method: "POST",
      }),

    /**
     * (Ri)genera solo il campo {@code contenuto} (≤ 600 char con citazione
     * verbatim) riutilizzando la pipeline C1-C8. NON tocca titolo, autori,
     * data o categoria: se l'editore li ha corretti a mano, il suo lavoro è
     * preservato. Restituisce l'approfondimento aggiornato incluso il nuovo
     * {@code descriptionSource} e il punteggio di {@code extractionConfidence*}.
     *
     * Può richiedere 10-30s: l'endpoint rifa estrazione HTML + OCR + call AI.
     */
    regenerateContenuto: (id: number) =>
      mutate<ApprofondimentoDto>(`/approfondimenti/by-id/${id}/ai/regenerate-contenuto`, {
        method: "POST",
      }),

    /** Associa un'immagine manuale gia' caricata come Media. */
    setImmagine: (id: number, mediaId: number) =>
      mutate<ApprofondimentoDto>(`/approfondimenti/by-id/${id}/immagine`, {
        method: "PATCH",
        body: JSON.stringify({ mediaId }),
      }),

    /** Rimuove l'immagine manuale (resta lo screenshot se presente). */
    clearImmagine: (id: number) =>
      mutate<ApprofondimentoDto>(`/approfondimenti/by-id/${id}/immagine`, {
        method: "DELETE",
      }),

    /** Associa un PDF allegato gia' caricato come Media. */
    setPdfAllegato: (id: number, mediaId: number) =>
      mutate<ApprofondimentoDto>(`/approfondimenti/by-id/${id}/pdf`, {
        method: "PATCH",
        body: JSON.stringify({ mediaId }),
      }),

    /** Rimuove il riferimento al PDF allegato. */
    clearPdfAllegato: (id: number) =>
      mutate<ApprofondimentoDto>(`/approfondimenti/by-id/${id}/pdf`, {
        method: "DELETE",
      }),

    /**
     * Pulisce i file di supporto (screenshot corrente + HTML prefetch
     * vecchi) per un singolo approfondimento. Cancella sia il record
     * Media dello screenshot sia il file PNG su disco. Non tocca
     * `immagine` (upload manuale) ne' i campi testuali.
     *
     * Restituisce un riassunto coi file rimossi e i byte liberati.
     */
    cleanup: (id: number) =>
      mutate<MediaCleanupResult>(`/approfondimenti/by-id/${id}/cleanup`, {
        method: "POST",
      }),

    /**
     * Pubblica (o ri-pubblica) un approfondimento su Facebook o Instagram via
     * Meta Graph API. Backend: POST /api/approfondimenti/by-id/{id}/social/{network}.
     * Non throwa: ritorna sempre un {@link SocialPublishResult} strutturato.
     */
    publishSocial: (id: number, network: SocialNetwork) =>
      socialPublish(`/approfondimenti/by-id/${id}/social/${network}`),
  },

  // ── Eventi esterni (suggeriti) ──────────────────────────────────
  eventiEsterni: {
    list: (query?: { page?: number; size?: number; filter?: "upcoming" | "past" | "all" }) =>
      request<PageResponse<EventoEsternoDto>>("/eventi-esterni", { query }),
    bySlug: (slug: string) => request<EventoEsternoDto>(`/eventi-esterni/_by-slug/${slug}`),
    homepage: (hero = false, limit = 3) =>
      request<EventoEsternoDto[]>("/eventi-esterni/_homepage", { query: { hero, limit } }),
    create: (data: Partial<EventoEsternoDto>) =>
      mutate<EventoEsternoDto>("/eventi-esterni", { method: "POST", body: JSON.stringify(data) }),
    update: (id: string, data: Partial<EventoEsternoDto>) =>
      mutate<EventoEsternoDto>(`/eventi-esterni/_by-id/${id}`, { method: "PUT", body: JSON.stringify(data) }),
    remove: (id: string) =>
      mutate<void>(`/eventi-esterni/_by-id/${id}`, { method: "DELETE" }),
    publish: (id: string) =>
      mutate<EventoEsternoDto>(`/eventi-esterni/${id}/_publish`, { method: "POST" }),
  },

  // ── Eventi ──────────────────────────────────────────────────────
  eventi: {
    list: (query?: { page?: number; size?: number; filter?: "upcoming" | "past" | "all" }) =>
      request<PageResponse<EventoDto>>("/eventi", { query }),
    bySlug: (slug: string) => request<EventoDto>(`/eventi/${slug}`),
    byRelatore: (slug: string, query?: { page?: number; size?: number }) =>
      request<PageResponse<EventoDto>>(`/eventi/relatore/${slug}`, { query }),
    daNonPerdere: (limit = 5) =>
      request<EventoDto[]>("/eventi/da-non-perdere", { query: { limit } }),
    create: (data: EventoInput) =>
      mutate<EventoDto>("/eventi", { method: "POST", body: JSON.stringify(data) }),
    update: (slug: string, data: EventoInput) =>
      mutate<EventoDto>(`/eventi/${slug}`, { method: "PUT", body: JSON.stringify(data) }),
    delete: (slug: string) =>
      mutate<void>(`/eventi/${slug}`, { method: "DELETE" }),

    /**
     * E5 — Cambia status (DRAFT/PUBLISHED/PRIVATE/SCHEDULED/TRASH).
     * Sulla transition verso PUBLISHED il backend triggera anche la
     * pubblicazione su Facebook + Instagram (best-effort, non blocca).
     */
    changeStatus: (slug: string, newStatus: "DRAFT" | "PUBLISHED" | "PRIVATE" | "SCHEDULED" | "TRASH") =>
      mutate<EventoDto>(`/eventi/${slug}/status`, {
        method: "PATCH",
        body: JSON.stringify({ status: newStatus }),
      }),

    /**
     * E4 — URL pubblico del file .ics scaricabile per importare l'evento
     * in Google/Apple/Outlook Calendar. Non c'è auth: l'endpoint è public.
     */
    calendarIcsUrl: (slug: string) => `/api/eventi/${slug}/calendar.ics`,

    // V13: allegati media generici ---------------------------------
    media: {
      /** Elenco allegati ordinati per position. Pubblico. */
      list: (slug: string) =>
        request<EventoMediaItemDto[]>(`/eventi/${slug}/media`),

      /**
       * Allega un file gia' caricato o un URL esterno. Per VIDEO_URL
       * passare solo `externalUrl`; per tutti gli altri tipi prima
       * caricare con api.media.upload(...) e poi passare `mediaId`.
       */
      attach: (
        slug: string,
        body: {
          mediaType: EventoMediaType;
          mediaId?: number;
          externalUrl?: string;
          title?: string;
          caption?: string;
          position?: number;
        }
      ) =>
        mutate<EventoMediaItemDto>(`/eventi/${slug}/media`, {
          method: "POST",
          body: JSON.stringify(body),
        }),

      /** Aggiorna title/caption/position. Non cambia tipo o sorgente. */
      update: (
        slug: string,
        mediaItemId: number,
        body: { title?: string; caption?: string; position?: number }
      ) =>
        mutate<EventoMediaItemDto>(`/eventi/${slug}/media/${mediaItemId}`, {
          method: "PATCH",
          body: JSON.stringify(body),
        }),

      detach: (slug: string, mediaItemId: number) =>
        mutate<void>(`/eventi/${slug}/media/${mediaItemId}`, { method: "DELETE" }),

      /**
       * Riordina in blocco: passare gli id nell'ordine desiderato. Il backend
       * ritorna l'Evento autoritativo (con i media riordinati), non l'array di
       * item: il chiamante usa lo stato ottimistico e ignora il ritorno.
       */
      reorder: (slug: string, orderedIds: number[]) =>
        mutate<EventoDto>(`/eventi/${slug}/media/reorder`, {
          method: "PUT",
          body: JSON.stringify({ orderedIds }),
        }),
    },

    /**
     * AI on-demand: ricerca articoli di stampa (e altri allegati pubblici)
     * relativi a un evento. Riusa la pipeline AI degli approfondimenti
     * (Gemini / Groq / OpenRouter con fallback) prendendo come input
     * titolo + data + luogo + relatori dell'evento. NON allega nulla
     * automaticamente: ritorna solo i candidati che l'editore deve
     * confermare.
     *
     * Operazione lenta: 20-40s tipici.
     */
    ai: {
      pressSearch: (slug: string) =>
        mutate<EventoPressSearchResultDto>(`/eventi/${slug}/ai/press-search`, {
          method: "POST",
        }),
    },

    /**
     * Pubblica (o ri-pubblica) un evento su Facebook o Instagram via Meta
     * Graph API. Backend: POST /api/eventi/{slug}/social/{network}.
     * Non throwa: ritorna sempre un {@link SocialPublishResult} strutturato.
     */
    publishSocial: (slug: string, network: SocialNetwork) =>
      socialPublish(`/eventi/${slug}/social/${network}`),
  },

  // ── Relatori ────────────────────────────────────────────────────
  relatori: {
    list: () => request<RelatoreDto[]>("/relatori"),
    withEventCount: () => request<RelatoreWithCountDto[]>("/relatori/with-event-count"),
    bySlug: (slug: string) => request<RelatoreDto>(`/relatori/${slug}`),
    /**
     * SEO: topics estratti via AI dai titoli/descrizioni degli eventi del
     * relatore. Backend (RelatoreTopicsService) cache TTL 6h: prima chiamata
     * 2-5s di latency (chain Gemini → Cerebras → Mistral → ...), chiamate
     * successive entro la finestra TTL ritornano in &lt;1ms.
     * Lista vuota se il relatore non ha eventi o se tutti i provider AI
     * falliscono (degrade graceful — la pagina relatore funziona comunque).
     */
    topics: (slug: string) => request<{ topics: string[] }>(`/relatori/${slug}/topics`),
    /** Setta o cancella l'override editoriale dei topics. V31. Admin. */
    setTopicsOverride: (slug: string, topics: string[]) =>
      mutate<{ topics: string[] }>(`/relatori/${slug}/topics`, {
        method: "PUT",
        body: JSON.stringify({ topics }),
      }),
    /** Lista admin TUTTI i relatori (anche hub_enabled=false). V32. */
    adminList: () => mutate<RelatoreDto[]>("/relatori/admin/all", { method: "GET" }),
    /** Get singolo admin (anche se hub_enabled=false). V32. */
    adminBySlug: (slug: string) =>
      mutate<RelatoreDto>(`/relatori/${slug}/admin`, { method: "GET" }),
    create: (data: RelatoreInput) =>
      mutate<RelatoreDto>("/relatori", { method: "POST", body: JSON.stringify(data) }),
    update: (slug: string, data: RelatoreInput) =>
      mutate<RelatoreDto>(`/relatori/${slug}`, { method: "PUT", body: JSON.stringify(data) }),
    delete: (slug: string) =>
      mutate<void>(`/relatori/${slug}`, { method: "DELETE" }),
  },

  // ── Autori (esterni, derivati dai campi approfondimenti.autori) ──
  /**
   * Gli autori NON sono entita' DB — sono stringhe libere split dal campo
   * autori degli approfondimenti. Lo slug e' derivato deterministicamente
   * lato backend (slugify lowercase + strip accenti).
   */
  autori: {
    /** Lista distinct autori PUBBLICI (DESC per piu' citati). V32: skip hub_enabled=false. */
    list: () => request<Array<{ nome: string; slug: string; articleCount: number; hubEnabled: boolean }>>(`/autori`),
    /** Lista admin TUTTI gli autori (anche disabilitati). V32. */
    adminList: () =>
      mutate<Array<{ nome: string; slug: string; articleCount: number; hubEnabled: boolean }>>(
        "/autori/admin/all",
        { method: "GET" },
      ),
    /** Dettaglio autore by slug. 404 se lo slug non matcha nessun autore o se hub_enabled=false. */
    bySlug: (slug: string) =>
      request<{ nome: string; slug: string; articleCount: number }>(`/autori/${slug}`),
    /** Articoli scritti/citati dall'autore (forma summary). */
    articoli: (slug: string) => request<ApprofondimentoDto[]>(`/autori/${slug}/articoli`),
    /** Topics AI-extracted — cache backend 6h. */
    topics: (slug: string) => request<{ topics: string[] }>(`/autori/${slug}/topics`),
    /** Info override editoriale completa (topics + hubEnabled). V31/V32. Admin. */
    getOverride: (slug: string) =>
      mutate<{ topics: string[]; hubEnabled: boolean }>(
        `/autori/${slug}/override`,
        { method: "GET" },
      ),
    /** Setta override topics (UPSERT autori_override). V31. Admin. */
    setTopicsOverride: (slug: string, topics: string[]) =>
      mutate<{ topics: string[] }>(`/autori/${slug}/topics`, {
        method: "PUT",
        body: JSON.stringify({ topics }),
      }),
    /** Toggle hub_enabled (on/off pagina pubblica). V32. Admin. */
    setHubEnabled: (slug: string, hubEnabled: boolean) =>
      mutate<{ topics: string[]; hubEnabled: boolean }>(`/autori/${slug}/hub`, {
        method: "PUT",
        body: JSON.stringify({ hubEnabled }),
      }),
  },

  // ── SEO operations (admin) ────────────────────────────────────
  /**
   * Operazioni SEO amministrative. Tutti gli endpoint richiedono auth admin
   * (validata dal proxy /api/cms/[...path] tramite session cookie Auth.js).
   */
  seo: {
    /**
     * Pinga Bing/Yandex/DuckDuckGo via IndexNow con TUTTI gli URL pubblici
     * del sito (pagine statiche + eventi + approfondimenti PUBLISHED).
     * Backend cap a 50 URL per batch, ping fire-and-forget.
     * No-op se {@code app.indexnow.key} non e' configurata sul backend
     * (in quel caso la response.urlsSent e' 0).
     */
    indexnowPingAll: () =>
      mutate<{ urlsSent: number; message: string }>(
        "/seo/indexnow/ping-all",
        { method: "POST" },
      ),
  },

  // ── Media ─────────────────────────────────────────────────────
  media: {
    /** Upload file via proxy autenticato. Ritorna il MediaDto creato. */
    upload: async (file: File): Promise<MediaDto> => {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch("/api/cms/media", {
        method: "POST",
        body: fd,
        // Content-Type viene settato automaticamente dal browser con boundary
      });
      if (!res.ok) {
        if (handleExpiredSession(res)) {
          return new Promise(() => {});
        }
        const body = await res.text().catch(() => "");
        throw new Error(
          body
            ? `Upload fallito ${res.status}: ${body.slice(0, 300)}`
            : `Upload fallito ${res.status} ${res.statusText}`,
        );
      }
      return res.json() as Promise<MediaDto>;
    },
    downloadUrl: (id: number) => `/cms-api/media/${id}`,
  },

  // ── Media cleanup (volume housekeeping) ─────────────────────
  //
  // Pulizia file orfani (PNG screenshot non referenziati + HTML prefetch
  // vecchi) sul volume condiviso col microservizio screenshot-service.
  // La UI admin chiama PRIMA preview() per mostrare i candidati, poi
  // run() per cancellare davvero. Lo schedule oraria e' off di default:
  // l'admin lo attiva esplicitamente da /admin/media-cleanup.
  mediaCleanup: {
    /** Dry-run: ritorna i file che sarebbero rimossi, senza cancellarli. */
    preview: () =>
      mutate<MediaCleanupResult>("/admin/media-cleanup/preview", { method: "GET" }),

    /** Esegue la pulizia globale (PNG orfani + HTML vecchi). */
    run: () =>
      mutate<MediaCleanupResult>("/admin/media-cleanup", { method: "POST" }),

    /** Stato della schedulazione oraria + snapshot ultimo run. */
    getSchedule: () =>
      mutate<MediaCleanupScheduleState>("/admin/media-cleanup/schedule", { method: "GET" }),

    /** Attiva/disattiva la schedulazione oraria a caldo. */
    setSchedule: (active: boolean) =>
      mutate<{ active: boolean }>("/admin/media-cleanup/schedule", {
        method: "PUT",
        body: JSON.stringify({ active }),
      }),
  },

  // ── WhatsApp avatar (Q-FIX2): foto profilo / logo gruppo via WAHA ─
  whatsappAvatar: {
    /**
     * Recupera l'URL dell'avatar di una chat WhatsApp. Backend chiama WAHA
     * via REST e ritorna {url: string} (stringa vuota se non disponibile).
     */
    fetch: (chatId: string) =>
      mutate<{ url: string }>(
        `/admin/whatsapp/avatar/${encodeURIComponent(chatId)}`,
        { method: "GET" },
      ),
  },

  // ── Queue monitor (Q-PR4: Artemis stats + DLQ browse) ───────
  queueMonitor: {
    /** Depth main + DLQ per ogni stage della pipeline a code. */
    stats: () =>
      mutate<Record<string, { main: number; dlq: number; reachable: boolean }>>(
        "/admin/queue/stats",
        { method: "GET" },
      ),
    /** Preview messaggi pendenti in una DLQ (max 1000). */
    dlq: (name: string) =>
      mutate<Array<{ correlationId: string | null; bodyPreview: string | null }>>(
        `/admin/queue/dlq/${encodeURIComponent(name)}`,
        { method: "GET" },
      ),
  },

  // ── AI Stats (per-provider citation rate, post-A) ────────────
  // Aggrega gli approfondimenti per (ai_provider, description_source) e
  // restituisce per ogni provider il citation rate. Filtro temporale
  // opzionale via `?days=N` (ultimi N giorni); senza parametro = tutto
  // lo storico.
  aiStats: {
    /** Statistiche aggregate per provider AI. */
    fetch: (days?: number) => {
      const qs = days && days > 0 ? `?days=${days}` : "";
      return mutate<AiStatsResponse>(`/admin/ai-stats${qs}`, { method: "GET" });
    },
  },

  // ── AI Provider Chain (ordine + enable/disable, TEXT e OCR) ──────
  aiChain: {
    /** Ordine effettivo dei provider per ogni scope. */
    get: () => mutate<AiChainSettings>("/admin/ai/chain", { method: "GET" }),

    /** Salva l'ordine/abilitazione di uno scope; ritorna lo stato aggiornato. */
    update: (scope: AiChainScope, providers: AiChainProviderEntry[]) =>
      mutate<AiChainProviderEntry[]>(`/admin/ai/chain/${scope}`, {
        method: "PUT",
        body: JSON.stringify({ providers }),
      }),
  },

  // ── AI Providers (registry dinamico: aggiungi/riconfigura/disabilita) ──
  aiProviders: {
    /** Tutti i provider configurati da admin (senza API key). */
    list: () => mutate<AiProviderConfig[]>("/admin/ai/providers", { method: "GET" }),

    /** Crea o aggiorna un provider (api key vuota = mantieni esistente). */
    upsert: (name: string, data: AiProviderConfigInput) =>
      mutate<AiProviderConfig>(`/admin/ai/providers/${encodeURIComponent(name)}`, {
        method: "PUT",
        body: JSON.stringify(data),
      }),

    /** Elimina un provider. */
    remove: (name: string) =>
      mutate<void>(`/admin/ai/providers/${encodeURIComponent(name)}`, { method: "DELETE" }),
  },

  // ── App Settings (notifiche, monitoraggio WhatsApp, ecc.) ────────
  settings: {
    /** Tutte le impostazioni amministrative (segreti mascherati). */
    list: () => mutate<AppSettingView[]>("/admin/settings", { method: "GET" }),

    /** Aggiorna un'impostazione; per le critiche serve confirm=true. */
    update: (key: string, value: string, confirm = false) =>
      mutate<AppSettingView>(
        `/admin/settings/${encodeURIComponent(key)}?confirm=${confirm}`,
        { method: "PUT", body: JSON.stringify({ value }) },
      ),

    /** Reset al valore env/default (rimuove l'override). */
    reset: (key: string) =>
      mutate<void>(`/admin/settings/${encodeURIComponent(key)}`, { method: "DELETE" }),
  },

  // ── AI Feedback ─────────────────────────────────────────────
  aiFeedback: {
    list: (query?: { page?: number; size?: number }) =>
      mutate<AiFeedbackDto[]>(`/ai-feedback${query ? '?' + new URLSearchParams(
        Object.fromEntries(Object.entries(query).filter(([, v]) => v !== undefined).map(([k, v]) => [k, String(v)]))
      ).toString() : ''}`, { method: "GET" }),
    byApprofondimento: (id: number) =>
      mutate<AiFeedbackDto[]>(`/ai-feedback/approfondimento/${id}`, { method: "GET" }),
    stats: () => mutate<AiFeedbackStats>("/ai-feedback/stats", { method: "GET" }),
    submit: (data: AiFeedbackInput) =>
      mutate<AiFeedbackDto>("/ai-feedback", { method: "POST", body: JSON.stringify(data) }),
  },

  // ── Home Blocks (V15: gallery homepage) ────────────────────────
  //
  // NOTA IMPORTANTE SUL BACKEND:
  //   Lo schema OpenAPI dichiara `configJson: string` (NON un oggetto JSON) —
  //   il backend lo memorizza come colonna text e lo restituisce così com'è.
  //   Il frontend però lo preferisce come oggetto (per accesso tipato a
  //   `.layout`, `.columns`, ecc.): quindi serializziamo/deserializziamo in
  //   questo layer in modo trasparente per i consumer.
  homeBlocks: {
    /** Lista pubblica: solo blocchi enabled=true, ordinati per position, con items. */
    listPublic: () => request<HomeBlockDto[]>("/home-blocks").then(parseHomeBlocks),

    /** Lista completa admin (include disabilitati). */
    adminList: () =>
      mutate<HomeBlockDto[]>("/home-blocks/admin", { method: "GET" }).then(parseHomeBlocks),

    adminGet: (id: number) =>
      mutate<HomeBlockDto>(`/home-blocks/${id}`, { method: "GET" }).then(parseHomeBlock),

    create: (data: HomeBlockInput) =>
      mutate<HomeBlockDto>("/home-blocks", {
        method: "POST",
        body: JSON.stringify(stripNullish(serializeBlockInput(data))),
      }).then(parseHomeBlock),

    update: (id: number, data: HomeBlockInput) =>
      mutate<HomeBlockDto>(`/home-blocks/${id}`, {
        method: "PUT",
        body: JSON.stringify(stripNullish(serializeBlockInput(data))),
      }).then(parseHomeBlock),

    delete: (id: number) =>
      mutate<void>(`/home-blocks/${id}`, { method: "DELETE" }),

    /** Riordina i blocchi. Body: `{ orderedIds: number[] }`. */
    reorder: (orderedIds: number[]) =>
      mutate<HomeBlockDto[]>("/home-blocks/reorder", {
        method: "PUT",
        body: JSON.stringify({ orderedIds }),
      }).then(parseHomeBlocks),

    items: {
      // NB: questi endpoint del backend ritornano SEMPRE il blocco autoritativo
      // (HomeBlockDto con `.items` aggiornati), non l'item singolo. Per questo
      // parso il blocco e i chiamanti leggono `block.items`.
      create: (blockId: number, data: HomeBlockItemInput) =>
        mutate<HomeBlockDto>(`/home-blocks/${blockId}/items`, {
          method: "POST",
          body: JSON.stringify(stripNullish(serializeItemInput(data))),
        }).then(parseHomeBlock),

      update: (itemId: number, data: HomeBlockItemInput) =>
        mutate<HomeBlockDto>(`/home-blocks/items/${itemId}`, {
          method: "PUT",
          body: JSON.stringify(stripNullish(serializeItemInput(data))),
        }).then(parseHomeBlock),

      delete: (itemId: number) =>
        mutate<void>(`/home-blocks/items/${itemId}`, { method: "DELETE" }),

      /** Riordina items di un blocco. Ritorna il blocco autoritativo. */
      reorder: (blockId: number, orderedIds: number[]) =>
        mutate<HomeBlockDto>(`/home-blocks/${blockId}/items/reorder`, {
          method: "PUT",
          body: JSON.stringify({ orderedIds }),
        }).then(parseHomeBlock),
    },
  },

  // ── WhatsApp ────────────────────────────────────────────────
  whatsapp: {
    healthCheck: () => request<WhatsAppHealthDto>("/webhooks/whatsapp"),

    /**
     * Lista admin degli URL ricevuti via WhatsApp con associato:
     *  - mittente (senderName / pushName) e chat
     *  - approfondimento creato (slug, titolo, status)
     *  - risposta outbound del bot con stato spunte (ack 0..4)
     *
     * Prova diversi endpoint candidati (il backend li sta finalizzando, cfr. step 8):
     *    GET /api/admin/whatsapp/processed-urls
     *    GET /api/admin/whatsapp/notifications
     *    GET /api/whatsapp/processed-urls
     * Se nessuno risponde, restituisce lista vuota (la UI mostra banner "in arrivo").
     */
    listProcessedUrls: async (query?: { page?: number; size?: number }) => {
      const qs = query ? "?" + new URLSearchParams(
        Object.fromEntries(
          Object.entries(query).filter(([, v]) => v !== undefined).map(([k, v]) => [k, String(v)])
        )
      ).toString() : "";
      const candidates = [
        `/admin/whatsapp/processed-urls${qs}`,
        `/admin/whatsapp/notifications${qs}`,
        `/whatsapp/admin/processed-urls${qs}`,
        `/whatsapp/processed-urls${qs}`,
      ];
      for (const path of candidates) {
        try {
          return await mutate<PageResponse<WhatsAppProcessedUrlDto> | WhatsAppProcessedUrlDto[]>(
            path, { method: "GET" }
          );
        } catch (err) {
          const msg = err instanceof Error ? err.message : "";
          if (msg.includes("404") || msg.includes("Not Found")) continue;
          throw err;
        }
      }
      return { items: [], page: 0, pageSize: 0, totalItems: 0, totalPages: 0 } as PageResponse<WhatsAppProcessedUrlDto>;
    },

    /**
     * Lista dei messaggi WhatsApp grezzi (se il backend li espone).
     * Endpoint atteso: GET /api/whatsapp/messages
     */
    listMessages: async (query?: { page?: number; size?: number; chatId?: string }) => {
      try {
        return await mutate<PageResponse<WhatsAppMessageDto> | WhatsAppMessageDto[]>(
          `/whatsapp/messages${query ? "?" + new URLSearchParams(
            Object.fromEntries(
              Object.entries(query).filter(([, v]) => v !== undefined).map(([k, v]) => [k, String(v)])
            )
          ).toString() : ""}`,
          { method: "GET" }
        );
      } catch (err) {
        const msg = err instanceof Error ? err.message : "";
        if (msg.includes("404") || msg.includes("Not Found")) {
          return { items: [], page: 0, pageSize: 0, totalItems: 0, totalPages: 0 } as PageResponse<WhatsAppMessageDto>;
        }
        throw err;
      }
    },
  },

};

/**
 * Rimuove ricorsivamente le chiavi con valore null/undefined da un oggetto
 * prima della serializzazione JSON. Utile per payload verso backend Quarkus
 * dove la presenza di `null` può violare vincoli @NotNull/@NotBlank e
 * produce 400 con body vuoto (Bean Validation in prod mode).
 *
 * Non ricorsivo dentro gli array (gli elementi vengono lasciati così come sono:
 * se sono oggetti, servirebbe logica ad hoc — per ora non è necessario).
 */
function stripNullish<T extends object>(obj: T): Partial<T> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
    if (v === null || v === undefined) continue;
    out[k] = v;
  }
  return out as Partial<T>;
}

/* ── Home Blocks: adattamento configJson object ↔ string ────────────
 *
 * Lo schema backend (OpenAPI /q/openapi) dichiara `configJson: string` per
 * BlockRequest, ItemRequest, ItemUpdateRequest e HomeBlockDto/HomeBlockItemDto.
 * Il frontend preferisce oggetti per accesso tipato (layout, columns, ecc.),
 * quindi convertiamo in questo layer senza toccare i consumer.
 */

function tryParseJson<T = unknown>(s: string | null | undefined): T | null {
  if (s === null || s === undefined || s === "") return null;
  try { return JSON.parse(s) as T; } catch { return null; }
}

type RawHomeBlockItemDto = Omit<HomeBlockItemDto, "configJson"> & {
  configJson: string | Record<string, unknown> | null;
};

type RawHomeBlockDto = Omit<HomeBlockDto, "configJson" | "items"> & {
  configJson: string | HomeBlockConfig | null;
  items: RawHomeBlockItemDto[];
};

function parseHomeBlockItem(raw: HomeBlockItemDto | RawHomeBlockItemDto | null | undefined): HomeBlockItemDto {
  // Difensivo: lo spec OpenAPI non documenta il body delle risposte POST/PUT
  // — se il backend torna 200 con body vuoto, `mutate()` restituisce undefined.
  if (!raw || typeof raw !== "object") return raw as unknown as HomeBlockItemDto;
  const cfg = raw.configJson;
  return {
    ...raw,
    configJson: typeof cfg === "string" ? tryParseJson<Record<string, unknown>>(cfg) : (cfg ?? null),
  } as HomeBlockItemDto;
}

function parseHomeBlock(raw: HomeBlockDto | RawHomeBlockDto | null | undefined): HomeBlockDto {
  if (!raw || typeof raw !== "object") return raw as unknown as HomeBlockDto;
  const cfg = raw.configJson;
  return {
    ...raw,
    configJson: typeof cfg === "string" ? tryParseJson<HomeBlockConfig>(cfg) : (cfg ?? null),
    items: Array.isArray(raw.items) ? raw.items.map(parseHomeBlockItem) : [],
  } as HomeBlockDto;
}

function parseHomeBlocks(list: (HomeBlockDto | RawHomeBlockDto)[] | null | undefined): HomeBlockDto[] {
  if (!Array.isArray(list)) return [];
  return list.map(parseHomeBlock);
}

/**
 * Converte l'eventuale oggetto `configJson` in stringa JSON prima di inviare
 * al backend (che la salva come text). Accetta anche stringhe già serializzate
 * o valori null/undefined.
 */
function serializeConfigJson(
  cfg: HomeBlockConfig | Record<string, unknown> | string | null | undefined,
): string | undefined {
  if (cfg === null || cfg === undefined) return undefined;
  if (typeof cfg === "string") return cfg;
  return JSON.stringify(cfg);
}

function serializeBlockInput(data: HomeBlockInput): Record<string, unknown> {
  const { configJson, ...rest } = data;
  const out: Record<string, unknown> = { ...rest };
  const serialized = serializeConfigJson(configJson);
  if (serialized !== undefined) out.configJson = serialized;
  return out;
}

function serializeItemInput(data: HomeBlockItemInput): Record<string, unknown> {
  const { configJson, ...rest } = data;
  const out: Record<string, unknown> = { ...rest };
  const serialized = serializeConfigJson(configJson);
  if (serialized !== undefined) out.configJson = serialized;
  return out;
}

